import { useState, useCallback } from 'react'
import { Search, ChevronUp, ChevronDown, ChevronsUpDown, ChevronLeft, ChevronRight } from 'lucide-react'
import { PageHeader } from '../components/ui/PageHeader'
import { useOrders, Order, OrdersFilters } from '../hooks/useOrders'
import { OrderDetailDrawer } from '../components/orders/OrderDetailDrawer'
import { formatKpiEur } from '../lib/formatters'

// ---------------------------------------------------------------------------
// Costanti
// ---------------------------------------------------------------------------

const STATUS_OPTIONS = [
  { value: '', label: 'Tutti gli stati' },
  { value: 'confirmed', label: 'Confermata' },
  { value: 'probable', label: 'Probabile' },
  { value: 'in_progress', label: 'In corso' },
  { value: 'delivered', label: 'Consegnata' },
  { value: 'invoiced', label: 'Fatturata' },
  { value: 'lost', label: 'Persa' },
  { value: 'cancelled', label: 'Annullata' },
]

const STATUS_COLORS: Record<string, string> = {
  confirmed: 'bg-blue-500/20 text-blue-300',
  probable: 'bg-yellow-500/20 text-yellow-300',
  in_progress: 'bg-emerald-500/20 text-emerald-300',
  delivered: 'bg-teal-500/20 text-teal-300',
  invoiced: 'bg-violet-500/20 text-violet-300',
  lost: 'bg-red-500/20 text-red-300',
  cancelled: 'bg-gray-500/20 text-gray-400',
}

const STATUS_LABELS: Record<string, string> = {
  confirmed: 'Confermata',
  probable: 'Probabile',
  in_progress: 'In corso',
  delivered: 'Consegnata',
  invoiced: 'Fatturata',
  lost: 'Persa',
  cancelled: 'Annullata',
}

const PAGE_SIZE = 50

type SortField = 'order_date' | 'order_value' | 'customer_name' | 'status'
type SortDir = 'asc' | 'desc'

// ---------------------------------------------------------------------------
// SortIcon
// ---------------------------------------------------------------------------

function SortIcon({ field, active, dir }: { field: SortField; active: SortField; dir: SortDir }) {
  if (field !== active) return <ChevronsUpDown size={13} className="text-white/20" />
  return dir === 'asc'
    ? <ChevronUp size={13} className="text-blue-400" />
    : <ChevronDown size={13} className="text-blue-400" />
}

// ---------------------------------------------------------------------------
// Orders page
// ---------------------------------------------------------------------------

export function Orders() {
  // Filtri
  const [search, setSearch] = useState('')
  const [status, setStatus] = useState('')
  const [fromDate, setFromDate] = useState('')
  const [toDate, setToDate] = useState('')
  const [customerName, setCustomerName] = useState('')

  // Paginazione + ordinamento lato client (la lista API è già ordinata per data DESC)
  const [page, setPage] = useState(0)
  const [sortField, setSortField] = useState<SortField>('order_date')
  const [sortDir, setSortDir] = useState<SortDir>('desc')

  // Drawer dettaglio
  const [selectedId, setSelectedId] = useState<string | null>(null)

  const filters: OrdersFilters = {
    ...(search && { search }),
    ...(status && { status }),
    ...(fromDate && { from_date: fromDate }),
    ...(toDate && { to_date: toDate }),
    ...(customerName && { customer_name: customerName }),
    limit: PAGE_SIZE,
    offset: page * PAGE_SIZE,
  }

  const { data, isLoading, error } = useOrders(filters)

  const handleSort = useCallback((field: SortField) => {
    if (field === sortField) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc')
    } else {
      setSortField(field)
      setSortDir('desc')
    }
  }, [sortField])

  const handleReset = () => {
    setSearch('')
    setStatus('')
    setFromDate('')
    setToDate('')
    setCustomerName('')
    setPage(0)
  }

  const hasFilters = search || status || fromDate || toDate || customerName

  // Ordinamento lato client sui dati della pagina corrente
  const sortedItems = data
    ? [...data.items].sort((a, b) => {
        let va: string | number | null = null
        let vb: string | number | null = null
        if (sortField === 'order_date') { va = a.order_date ?? ''; vb = b.order_date ?? '' }
        if (sortField === 'order_value') { va = a.order_value ?? 0; vb = b.order_value ?? 0 }
        if (sortField === 'customer_name') { va = a.customer_name ?? ''; vb = b.customer_name ?? '' }
        if (sortField === 'status') { va = a.status; vb = b.status }
        if (va === vb) return 0
        const cmp = va! < vb! ? -1 : 1
        return sortDir === 'asc' ? cmp : -cmp
      })
    : []

  const totalPages = data ? Math.ceil(data.total / PAGE_SIZE) : 0

  return (
    <div className="space-y-6">
      <PageHeader title="Commesse" subtitle={data ? `${data.total} commesse totali` : undefined} />

      {/* Barra filtri */}
      <div className="bg-white/5 border border-white/10 rounded-xl p-4 space-y-3">
        <div className="flex flex-wrap gap-3">
          {/* Ricerca testuale */}
          <div className="relative flex-1 min-w-[200px]">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/30" />
            <input
              type="text"
              placeholder="Cerca per codice o descrizione…"
              value={search}
              onChange={e => { setSearch(e.target.value); setPage(0) }}
              className="w-full pl-9 pr-3 py-2 bg-white/5 border border-white/10 rounded-lg text-sm text-white/80 placeholder-white/30 focus:outline-none focus:border-blue-500/50"
            />
          </div>

          {/* Cliente */}
          <input
            type="text"
            placeholder="Filtra cliente…"
            value={customerName}
            onChange={e => { setCustomerName(e.target.value); setPage(0) }}
            className="min-w-[160px] px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-sm text-white/80 placeholder-white/30 focus:outline-none focus:border-blue-500/50"
          />

          {/* Stato */}
          <select
            value={status}
            onChange={e => { setStatus(e.target.value); setPage(0) }}
            className="px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-sm text-white/80 focus:outline-none focus:border-blue-500/50"
          >
            {STATUS_OPTIONS.map(o => (
              <option key={o.value} value={o.value} className="bg-[#1a1f35]">{o.label}</option>
            ))}
          </select>
        </div>

        <div className="flex flex-wrap gap-3 items-center">
          {/* Date range */}
          <input
            type="date"
            value={fromDate}
            onChange={e => { setFromDate(e.target.value); setPage(0) }}
            className="px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-sm text-white/60 focus:outline-none focus:border-blue-500/50"
          />
          <span className="text-white/30 text-sm">—</span>
          <input
            type="date"
            value={toDate}
            onChange={e => { setToDate(e.target.value); setPage(0) }}
            className="px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-sm text-white/60 focus:outline-none focus:border-blue-500/50"
          />

          {hasFilters && (
            <button
              onClick={handleReset}
              className="text-xs text-white/40 hover:text-white/70 underline transition-colors"
            >
              Azzera filtri
            </button>
          )}
        </div>
      </div>

      {/* Tabella */}
      <div className="bg-white/5 border border-white/10 rounded-xl overflow-hidden">
        {/* Loading skeleton */}
        {isLoading && (
          <div className="p-6 space-y-3 animate-pulse">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="h-10 bg-white/5 rounded" />
            ))}
          </div>
        )}

        {/* Errore */}
        {error && !isLoading && (
          <div className="p-8 text-center text-red-400 text-sm">
            Errore nel caricamento delle commesse. Riprova.
          </div>
        )}

        {/* Empty state */}
        {!isLoading && !error && data?.total === 0 && (
          <div className="p-12 text-center">
            <p className="text-white/40 text-sm">
              {hasFilters ? 'Nessuna commessa corrisponde ai filtri selezionati.' : 'Nessuna commessa disponibile.'}
            </p>
            {hasFilters && (
              <button onClick={handleReset} className="mt-2 text-xs text-blue-400 hover:text-blue-300 underline">
                Azzera filtri
              </button>
            )}
          </div>
        )}

        {/* Tabella */}
        {!isLoading && !error && (sortedItems.length > 0) && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-white/10 text-white/40 text-xs">
                  <th
                    className="px-4 py-3 text-left font-medium cursor-pointer hover:text-white/70 select-none whitespace-nowrap"
                    onClick={() => handleSort('customer_name')}
                  >
                    <span className="flex items-center gap-1">
                      Cliente <SortIcon field="customer_name" active={sortField} dir={sortDir} />
                    </span>
                  </th>
                  <th className="px-4 py-3 text-left font-medium whitespace-nowrap">Codice</th>
                  <th
                    className="px-4 py-3 text-left font-medium cursor-pointer hover:text-white/70 select-none whitespace-nowrap"
                    onClick={() => handleSort('order_date')}
                  >
                    <span className="flex items-center gap-1">
                      Data <SortIcon field="order_date" active={sortField} dir={sortDir} />
                    </span>
                  </th>
                  <th
                    className="px-4 py-3 text-left font-medium cursor-pointer hover:text-white/70 select-none whitespace-nowrap"
                    onClick={() => handleSort('status')}
                  >
                    <span className="flex items-center gap-1">
                      Stato <SortIcon field="status" active={sortField} dir={sortDir} />
                    </span>
                  </th>
                  <th className="px-4 py-3 text-left font-medium whitespace-nowrap">Service line</th>
                  <th
                    className="px-4 py-3 text-right font-medium cursor-pointer hover:text-white/70 select-none whitespace-nowrap"
                    onClick={() => handleSort('order_value')}
                  >
                    <span className="flex items-center gap-1 justify-end">
                      Valore <SortIcon field="order_value" active={sortField} dir={sortDir} />
                    </span>
                  </th>
                  <th className="px-4 py-3 text-right font-medium whitespace-nowrap">Budget ore</th>
                  <th className="px-4 py-3 text-left font-medium whitespace-nowrap hidden lg:table-cell">Descrizione</th>
                </tr>
              </thead>
              <tbody>
                {sortedItems.map((order: Order) => (
                  <tr
                    key={order.id}
                    onClick={() => setSelectedId(order.id)}
                    className="border-b border-white/5 hover:bg-white/5 cursor-pointer transition-colors"
                  >
                    <td className="px-4 py-3 text-white/90 font-medium">
                      {order.customer_name ?? '—'}
                    </td>
                    <td className="px-4 py-3 text-white/50 font-mono text-xs">
                      {order.external_id ?? order.id.slice(0, 8)}
                    </td>
                    <td className="px-4 py-3 text-white/60 whitespace-nowrap">
                      {order.order_date ?? '—'}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${STATUS_COLORS[order.status] ?? 'bg-gray-500/20 text-gray-400'}`}>
                        {STATUS_LABELS[order.status] ?? order.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-white/50 text-xs">
                      {order.service_line ?? '—'}
                    </td>
                    <td className="px-4 py-3 text-right text-white/80 font-medium tabular-nums whitespace-nowrap">
                      {order.order_value != null ? formatKpiEur(order.order_value) : '—'}
                    </td>
                    <td className="px-4 py-3 text-right text-white/50 tabular-nums">
                      {order.budget_hours != null ? `${order.budget_hours.toFixed(0)} h` : '—'}
                    </td>
                    <td className="px-4 py-3 text-white/40 text-xs truncate max-w-[200px] hidden lg:table-cell">
                      {order.description ?? '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Paginazione */}
        {!isLoading && data && data.total > PAGE_SIZE && (
          <div className="px-4 py-3 border-t border-white/10 flex items-center justify-between text-sm text-white/50">
            <span>{data.total} risultati · pagina {page + 1} di {totalPages}</span>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage(p => Math.max(0, p - 1))}
                disabled={page === 0}
                className="p-1.5 rounded hover:bg-white/10 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronLeft size={15} />
              </button>
              <button
                onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                disabled={page >= totalPages - 1}
                className="p-1.5 rounded hover:bg-white/10 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronRight size={15} />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Drawer dettaglio */}
      <OrderDetailDrawer
        orderId={selectedId}
        onClose={() => setSelectedId(null)}
      />
    </div>
  )
}
